import React, { useEffect, Suspense, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import { localizedPath, useTranslations } from './i18n';
import useLanguage from './hooks/useLanguage';
import googleAdsService from './services/googleAdsService';
import useScrollOptimization from './hooks/useScrollOptimization';
import useHashScroll from './hooks/useHashScroll';
import useMobileOptimizations from './hooks/useMobileOptimizations';
import { useAnalyticsTracking } from './hooks/useGoogleAnalytics';
import SEOHead from './components/SEOHead';
import SiteNav from './components/SiteNav';
import SiteFooter from './components/SiteFooter';
import LanguageSwitcher from './components/LanguageSwitcher';
import HeroBackgroundVideo from './components/HeroBackgroundVideo';
import SectionMedia from './components/SectionMedia';
import FaqSection from './components/FaqSection';
import AboutStoryVideo from './components/AboutStoryVideo';
import MapEmbed from './components/MapEmbed';
import { BUSINESS } from './seo/seo';

const GoogleReviews = React.lazy(() => import('./components/GoogleReviews'));
// const Gallery = React.lazy(() => import('./components/Gallery')); // Temporarily disabled
  


const MobileLoadingOptimizer = React.lazy(() => import('./components/MobileLoadingOptimizer'));

function App() {
  const { language: currentLanguage, changeLanguage: switchLanguage } = useLanguage();
  const analytics = useAnalyticsTracking();


  useScrollOptimization();
  useHashScroll();
  const { enableBodyScroll } = useMobileOptimizations();

  const t = useTranslations(currentLanguage);

  // Removed localization helpers (unused)

  // Handle directions click with conversion tracking
  const handleDirectionsClick = useCallback(() => {
    googleAdsService.trackGetDirections();
  }, []);

  // Handle phone call click with conversion tracking
  const handlePhoneClick = useCallback(() => {
    googleAdsService.trackPhoneCall();
  }, []);

  // Handle email click with conversion tracking
  const handleEmailClick = useCallback(() => {
    googleAdsService.trackContactForm('email');
  }, []);

  // Cleanup effect for modal state
  useEffect(() => {
    return () => {
      // Cleanup: ensure modal-open class is removed on unmount
      document.body.classList.remove('modal-open');
      enableBodyScroll();
    };
  }, [enableBodyScroll]);

  const changeLanguage = useCallback((lang) => {
    if (lang !== currentLanguage) {
      analytics.trackLanguageChange(lang, currentLanguage);
    }
    switchLanguage(lang);
  }, [currentLanguage, analytics, switchLanguage]);

  const menuPath = localizedPath('/menu', currentLanguage);

  return (
    <div className="App has-site-nav">
      {/* SEO Head Component */}
      <SEOHead language={currentLanguage} page="home" t={t} />

      <SiteNav
        t={t}
        currentLanguage={currentLanguage}
        onLanguageChange={changeLanguage}
      />
      
      {/* Mobile Loading Optimizer */}
      <Suspense fallback={null}>
        <MobileLoadingOptimizer />
      </Suspense>

      <main id="main">

      {/* Hero Section */}
      <section id="home" className="hero">
        {/* Animated Background */}
        <div className="hero-background">
          {/* Poster URLs live in App.css so media queries can pick the right size —
              see the matching preloads in public/index.html. */}
          <div
            className="hero-bg-image hero-bg-image--mobile"
            role="img"
            aria-label="Ullishtja Agroturizem restaurant and garden in Durrës, Albania"
          />
          <div className="hero-bg-image hero-bg-image--desktop" aria-hidden="true" />
          <HeroBackgroundVideo
            src="/videos/hero/hero-bg-mobile.mp4"
            poster="/images/posters/hero-poster-mobile-900.webp"
          />
          <div className="hero-overlay"></div>
          <div className="floating-elements">
            <div className="floating-element olive">🫒</div>
            <div className="floating-element food">🍽️</div>
            <div className="floating-element mountain">🏔️</div>
            <div className="floating-element wine">🍷</div>
            <div className="floating-element leaf">🌿</div>
          </div>
        </div>

        <div className="hero-container">
          <div className="hero-content">
            {/* <div className="hero-badge">
              <span className="badge-icon">⭐</span>
              <span className="badge-text">{t.hero.badge}</span>
              <span className="badge-icon">⭐</span>
            </div> */}
            
            <div className="hero-eyebrow">
              <span className="eyebrow-line"></span>
              <span className="eyebrow-text">Est. 2021 &middot; Durr&euml;s, Albania</span>
              <span className="eyebrow-line"></span>
            </div>
            
            <h1 className="hero-title">
              <span className="title-line title-main">{t.hero.title}</span>
            </h1>

            <div className="hero-gold-divider"></div>

            <p className="hero-description">
              {t.hero.description}
            </p>


            <div className="hero-actions">
              <div className="hero-actions-top">
                <Link to={menuPath} className="cta-button secondary">
                  <span className="btn-text">{t.hero.viewMenu}</span>
                </Link>
                <a
                  href="https://maps.google.com/?q=41.340278,19.433569+(Ullishtja+Agroturizem)"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button secondary"
                  onClick={handleDirectionsClick}
                >
                  <span className="btn-text">{t.footer.directions}</span>
                </a>
              </div>
              <a href="#contact" className="cta-button primary hero-cta-reservations">
                <span className="btn-text">{t.hero.cta}</span>
                <span className="btn-arrow">→</span>
              </a>

                <LanguageSwitcher
                  variant="hero"
                  currentLanguage={currentLanguage}
                  onLanguageChange={changeLanguage}
                />
            </div>
          </div>

        </div>

        <a href="#about" className="hero-scroll-hint">
          <span className="hero-view-more">{t.hero.viewMore}</span>
          <span className="hero-scroll-arrow" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4v13" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" />
              <path d="M6.5 13.5L12 19l5.5-5.5" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </a>

      </section>



      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 id="about-heading">{t.about.title}</h2>
              <p>
                {t.about.text1}
              </p>
              <p>
                {t.about.text2}
              </p>

            </div>
          </div>
        </div>
        <AboutStoryVideo alt={`${t.about.title} — Ullishtja Agroturizem`} />
      </section>



      {/* Events Section */}
      <section id="events" className="events-section">
        <div className="container">
          <div className="section-grid">
            <div className="section-image">
              <SectionMedia
                posterBase="events-poster"
                alt="Beautiful wedding and events venue at Ullishtja Agroturizem - Perfect for celebrations up to 120 guests with stunning Albanian mountain backdrop"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 42vw"
              />
              <div className="image-overlay">
                <div className="overlay-content">
                  <span className="overlay-icon">🎊</span>
                  <span className="overlay-text">Special Events</span>
                </div>
              </div>
            </div>
            <div className="section-content">
              <h2 id="events-heading">{t.events.title}</h2>
              <h3 className="section-subtitle">{t.events.subtitle}</h3>
              <p className="section-text">
                {t.events.text1}
              </p>
              <p className="section-text">
                {t.events.text2}
              </p>
              {/* <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">💒</span>
                <span className="feature-text">{t.events.features.weddings}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🏢</span>
                <span className="feature-text">{t.events.features.corporate}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎉</span>
                <span className="feature-text">{t.events.features.family}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👥</span>
                <span className="feature-text">{t.events.features.capacity}</span>
              </div>
            </div> */}
              <div className="menu-cta-container alacarte-cta">
                <a
                  href="/images/image_menu/events-menu.pdf"
                  className="mobile-menu-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.events.viewMenu}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A la Carte Section */}
      <section id="alacarte" className="alacarte-section">
        <div className="container">
          <div className="section-grid reverse">
            <div className="section-image">
              <SectionMedia
                posterBase="alacarte-poster"
                alt="Authentic Albanian a la carte cuisine featuring traditional recipes, fresh farm ingredients, and seasonal specialties at Ullishtja Agroturizem"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 42vw"
              />
              <div className="image-overlay">
                <div className="overlay-content">
                  <span className="overlay-icon">🍽️</span>
                  <span className="overlay-text">A la Carte</span>
                </div>
              </div>
            </div>
            <div className="section-content">
              <h2 id="alacarte-heading">{t.alacarte.title}</h2>
              <h3 className="section-subtitle">{t.alacarte.subtitle}</h3>
              <p className="section-text">
                {t.alacarte.text1}
              </p>
              <p className="section-text">
                {t.alacarte.text2}
              </p>
              {/* <div className="features-list">
                <div className="feature-item">
                  <span className="feature-icon">🌱</span>
                  <span className="feature-text">{t.alacarte.features.fresh}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📜</span>
                  <span className="feature-text">{t.alacarte.features.traditional}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🍂</span>
                  <span className="feature-text">{t.alacarte.features.seasonal}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🍷</span>
                  <span className="feature-text">{t.alacarte.features.wine}</span>
                </div>
              </div> */}

              {/* Moved CTAs here: show green on desktop/tablet, orange on mobile */}
              <div className="menu-cta-container alacarte-cta">
                <Link to={menuPath} className="mobile-menu-btn">
                  {t.hero.viewMenu}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      

      {/* Gallery Section - Temporarily Disabled */}
      {/* <Suspense fallback={<div className="loading-section">Loading gallery...</div>}>
        <Gallery currentLanguage={currentLanguage} translations={t} />
      </Suspense> */}

      {/* Google Reviews Section */}
      <Suspense fallback={<div className="loading-section">Loading reviews...</div>}>
        <GoogleReviews currentLanguage={currentLanguage} translations={t} />
      </Suspense>

      {/* Contact Section */}
      <section id="contact" className="contact contact-premium">
        <div className="container">
          <div className="contact-premium-header">
            <h2 id="contact-heading">{t.contact.title}</h2>
            <p className="contact-premium-subtitle">{t.contact.info.title}</p>
          </div>

          <div className="contact-premium-layout">
            <article className="contact-map-card">
              <h3 className="contact-block-title">{t.contact.info.address.title}</h3>
              <div className="contact-map-wrap">
                <MapEmbed
                  label={t.contact.showMap}
                  title="Ullishtja Agroturizem location map – Durrës, Albania"
                />
              </div>

              <address className="contact-location-copy">
                {BUSINESS.name}<br />
                {BUSINESS.streetAddress}<br />
                {BUSINESS.postalCode} {BUSINESS.locality}, Albania
              </address>

              <div className="contact-map-actions">
                <a
                  href="https://maps.google.com/?q=41.340278,19.433569+(Ullishtja+Agroturizem)"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-map-btn contact-map-btn-primary"
                  onClick={handleDirectionsClick}
                >
                  Google Maps
                </a>
                <a
                  href="http://maps.apple.com/?daddr=41.340278,19.433569&q=Ullishtja+Agroturizem"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-map-btn contact-map-btn-secondary"
                >
                  Apple Maps
                </a>
              </div>
            </article>

            <div className="contact-info-grid">
              <article className="contact-info-card">
                <h3 className="contact-block-title">{t.contact.info.phone.title}</h3>
                <a href="tel:+355684090405" className="contact-info-value" onClick={handlePhoneClick}>
                  {t.contact.info.phone.text}
                </a>
              </article>

              <article className="contact-info-card">
                <h3 className="contact-block-title">{t.contact.info.email.title}</h3>
                <a href="mailto:hi@ullishtja-agroturizem.com" className="contact-info-value" onClick={handleEmailClick}>
                  {t.contact.info.email.text}
                </a>
              </article>

              <article className="contact-info-card">
                <h3 className="contact-block-title">{t.contact.info.hours.title}</h3>
                <p className="contact-info-value contact-info-hours">
                  {t.contact.info.hours.text.split('\n').map((line, index) => (
                    <span key={index}>
                      {line}
                      {index < t.contact.info.hours.text.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </p>
              </article>

              <article className="contact-info-card">
                <h3 className="contact-block-title">{t.contact.info.whatsapp.title}</h3>
                <a
                  href="https://wa.me/355684090405"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-info-value"
                  onClick={() => {
                    googleAdsService.trackContactForm('whatsapp');
                    analytics.trackEvent('contact', 'whatsapp_click', 'contact_section');
                  }}
                >
                  {t.contact.info.whatsapp.text}
                </a>
              </article>
            </div>
          </div>
            {/* Reservation Form - Temporarily Disabled */}
            {/* <div className="reservation-form">
              <h3>{t.contact.reservation.title}</h3>
              
              {reservationStatus.loading && (
                <div className="status-message loading">
                  <span className="spinner">⏳</span> {t.contact.reservation.processing}
                </div>
              )}
              
              {reservationStatus.success && (
                <div className="status-message success">
                  <span className="icon">✅</span> {reservationStatus.message}
                </div>
              )}
              
              {reservationStatus.error && (
                <div className="status-message error">
                  <span className="icon">❌</span> {reservationStatus.error}
                </div>
              )}

              <form onSubmit={handleReservationSubmit}>
                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">👤</span>
                    {t.contact.reservation.name}
                  </label>
                  <input 
                    type="text" 
                    name="name"
                    placeholder={t.contact.reservation.name} 
                    required 
                    disabled={reservationStatus.loading}
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">📧</span>
                    {t.contact.reservation.email}
                  </label>
                  <input 
                    type="email" 
                    name="email"
                    placeholder={t.contact.reservation.email} 
                    required 
                    disabled={reservationStatus.loading}
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">📞</span>
                    {t.contact.reservation.phone}
                  </label>
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder={t.contact.reservation.phone} 
                    required 
                    disabled={reservationStatus.loading}
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">📅</span>
                    Date
                  </label>
                  <input 
                    type="date" 
                    name="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    required 
                    min={minDate}
                    disabled={reservationStatus.loading}
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">🕐</span>
                    Time
                  </label>
                  {loadingTimeSlots ? (
                    <div className="loading-slots">Loading available times...</div>
                  ) : !selectedDate ? (
                    <div className="select-date-first">Please select a date first</div>
                  ) : availableTimeSlots.length === 0 ? (
                    <div className="no-slots">No available time slots for this date</div>
                  ) : (
                    <select 
                      name="time"
                      required 
                      disabled={reservationStatus.loading}
                    >
                      <option value="">Select time</option>
                      {formattedTimeSlots.map(slot => {
                        return (
                          <option key={slot.id} value={slot.time}>
                            {slot.displayTime} (Available: {slot.availableCapacity} guests)
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>

                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">👥</span>
                    {t.contact.reservation.guests}
                  </label>
                  <select 
                    name="guests" 
                    required 
                    disabled={reservationStatus.loading}
                    onChange={handleGuestCountChange}
                    value={selectedGuests === 9 ? '9+' : selectedGuests}
                  >
                    <option value="">{t.contact.reservation.guests}</option>
                    <option value="1">1 {t.contact.reservation.guest}</option>
                    <option value="2">2 {t.contact.reservation.guests_plural}</option>
                    <option value="3">3 {t.contact.reservation.guests_plural}</option>
                    <option value="4">4 {t.contact.reservation.guests_plural}</option>
                    <option value="5">5 {t.contact.reservation.guests_plural}</option>
                    <option value="6">6 {t.contact.reservation.guests_plural}</option>
                    <option value="7">7 {t.contact.reservation.guests_plural}</option>
                    <option value="8">8 {t.contact.reservation.guests_plural}</option>
                    <option value="9+">9+ {t.contact.reservation.guests_plural}</option>
                  </select>
                  {isLargeGroup && (
                    <div className="large-group-notice">
                      <div className="notice-content">
                        <span className="notice-icon">📞</span>
                        <div className="notice-text">
                          <strong>Large Group Reservation</strong>
                          <p>For groups of 9+ guests, please contact us directly for special arrangements:</p>
                          <a href="tel:+355684090405" className="contact-link" onClick={handlePhoneClick}>📞 +355 68 409 0405</a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-field">
                  <label className="form-label">
                    <span className="label-icon">📝</span>
                    {t.contact.reservation.requests}
                  </label>
                  <textarea 
                    name="requests"
                    placeholder={t.contact.reservation.requests}
                    disabled={reservationStatus.loading}
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={reservationStatus.loading}
                >
                  {reservationStatus.loading ? t.contact.reservation.processing : t.contact.reservation.submit}
                </button>
              </form>
            </div> */}
        </div>
      </section>

      <FaqSection t={t} />
      </main>

      <SiteFooter t={t} currentLanguage={currentLanguage} onLanguageChange={changeLanguage} />

    </div>
  );
}

export default App;
